'use client'

import React, { useState } from 'react'

interface DraggableItem {
  id: number | string
  [key: string]: any
}

interface UseDragDropOptions {
  onOrderChange?: () => void
  ghostImageStyle?: Partial<CSSStyleDeclaration>
}

export function useDragDrop<T extends DraggableItem>(
  initialItems: T[],
  options: UseDragDropOptions = {}
) {
  const [items, setItems] = useState<T[]>(initialItems)
  const [draggedItem, setDraggedItem] = useState<T | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [orderChanged, setOrderChanged] = useState(false)

  const handleDragStart = (e: React.DragEvent, item: T, index: number) => {
    setDraggedItem(item)
    e.dataTransfer.effectAllowed = 'move'
    
    // Create ghost image
    const target = e.currentTarget as HTMLElement
    const row = target.closest('tr')
    if (row) {
      const clone = row.cloneNode(true) as HTMLElement
      clone.style.position = 'absolute'
      clone.style.top = '-9999px'
      clone.style.opacity = options.ghostImageStyle?.opacity || '0.8'
      clone.style.backgroundColor = options.ghostImageStyle?.backgroundColor || '#f0f4ff'
      document.body.appendChild(clone)
      e.dataTransfer.setDragImage(clone, 0, 0)
      setTimeout(() => document.body.removeChild(clone), 0)
    }
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'

    if (!draggedItem || dragOverIndex === index) return

    setDragOverIndex(index)

    // Reorder items in real-time
    const newItems = [...items]
    const dragIndex = newItems.findIndex(item => item.id === draggedItem.id)
    
    if (dragIndex !== index && dragIndex !== -1) {
      const [removed] = newItems.splice(dragIndex, 1)
      newItems.splice(index, 0, removed)
      setItems(newItems)
    }
  }

  const handleDragLeave = () => {
    setDragOverIndex(null)
  }

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()
    if (!draggedItem) return

    setOrderChanged(true)
    if (options.onOrderChange) {
      options.onOrderChange()
    }
    
    setDraggedItem(null)
    setDragOverIndex(null)
  }

  const handleDragEnd = () => {
    setDraggedItem(null)
    setDragOverIndex(null)
  }

  const resetOrderChanged = () => {
    setOrderChanged(false)
  }

  return {
    items,
    setItems,
    draggedItem,
    dragOverIndex,
    orderChanged,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
    resetOrderChanged
  }
}
